// frontend/src/modules/payroll/pages/PayslipsListPage.tsx
//
// Cross-run payslip list — `/dashboard/payroll/payslips`.
//
// The backend has no tenant-wide "list payslips" endpoint, so we fan out
// over all runs (see `payrollApi.listAllPayslips`). Filters are applied
// client-side. A "Per employee" toggle groups payslips by employee_id.
import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Banknote, Plus, Filter, Search, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { payrollApi } from '../api';
import type { Payslip, PayrollRun } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

type Row = Payslip & { run: PayrollRun };
type StatusFilter = 'all' | 'pending' | 'paid' | 'on_hold';
type Mode = 'flat' | 'per_employee';

export function PayslipsListPage() {
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  const [rows, setRows] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [failedRuns, setFailedRuns] = React.useState<number>(0);

  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const runsQ = useApi<{ runs: PayrollRun[] }>('/api/payroll/runs');

  // Filters
  const [employeeId, setEmployeeId] = React.useState<string>('all');
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [runId, setRunId] = React.useState<string>('all');
  const [from, setFrom] = React.useState<string>('');
  const [to, setTo] = React.useState<string>('');
  const [mode, setMode] = React.useState<Mode>('flat');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFailedRuns(0);
    let failures = 0;
    payrollApi.listAllPayslips(() => { failures += 1; })
      .then((all) => { if (!cancelled) { setRows(all); setFailedRuns(failures); } })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Permission gate — if the user can't list runs at all, surface a hint
  // pointing them to the adaptive Payroll page (which shows their own slips).
  React.useEffect(() => {
    if (runsQ.error) {
      toast.error('You don\'t have permission to view all payslips', {
        description: 'See your own payslips on the Payroll page.',
      });
    }
  }, [runsQ.error]);

  const employees = empQ.data?.employees ?? [];
  const runs = runsQ.data?.runs ?? [];

  const employeeById: Record<string, EmployeeWithUser> = {};
  for (const e of employees) employeeById[e.id] = e;

  const filtered = React.useMemo(() => {
    return rows.filter((r) => {
      if (employeeId !== 'all' && r.employee_id !== employeeId) return false;
      if (runId !== 'all' && r.run.id !== runId) return false;
      if (status !== 'all') {
        const tone = statusFor(r);
        if (status === 'paid' && tone.key !== 'paid') return false;
        if (status === 'pending' && tone.key !== 'pending') return false;
        if (status === 'on_hold' && tone.key !== 'on_hold') return false;
      }
      if (from) {
        if (new Date(r.run.period_end) < new Date(from)) return false;
      }
      if (to) {
        if (new Date(r.run.period_start) > new Date(to)) return false;
      }
      return true;
    });
  }, [rows, employeeId, runId, status, from, to]);

  const totals = React.useMemo(() => {
    let gross = 0, deductions = 0, net = 0;
    for (const r of filtered) {
      gross += Number(r.gross_additions);
      deductions += Number(r.gross_deductions);
      net += Number(r.net_amount);
    }
    return { gross, deductions, net };
  }, [filtered]);

  const hasAnyFilter = employeeId !== 'all' || runId !== 'all' || status !== 'all' || from !== '' || to !== '';

  return (
    <div>
      <PageHeader
        eyebrow="Team / Payroll / Payslips"
        title="Payslips"
        description="Every payslip across every run. Filter by employee, status, period, or run."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/dashboard/payroll">
              <Button variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <Banknote className="h-3.5 w-3.5" /> Runs
              </Button>
            </Link>
            <Link to="/dashboard/payroll/reports">
              <Button variant="ghost" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 text-muted-foreground">
                Reports <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* Totals strip from current filter */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SumTile label="Gross earnings" value={formatMoney(totals.gross, currency)} caption="Sum of additions" />
          <SumTile label="Total deductions" value={formatMoney(totals.deductions, currency)} caption="Across visible payslips" />
          <SumTile label="Net pay" value={formatMoney(totals.net, currency)} caption={`${filtered.length} payslip${filtered.length === 1 ? '' : 's'}`} accent />
        </section>

        {/* Filters */}
        <section className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full bg-white/65 dark:bg-white/[0.06] backdrop-blur-md shadow-pill text-[12px] font-semibold text-muted-foreground">
            <Filter className="h-3 w-3" /> Filter
          </div>

          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-10 w-auto min-w-[180px]">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger className="h-10 w-auto min-w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              {/* One bucket: a payslip whose run is PAID, or that was emailed. */}
              <SelectItem value="paid">Paid or emailed</SelectItem>
              <SelectItem value="on_hold">Not sent</SelectItem>
            </SelectContent>
          </Select>

          <Select value={runId} onValueChange={setRunId}>
            <SelectTrigger className="h-10 w-auto min-w-[200px]">
              <SelectValue placeholder="Run" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All runs</SelectItem>
              {runs.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {formatDate(r.period_start)} – {formatDate(r.period_end)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-white/65 dark:bg-white/[0.06] backdrop-blur-md shadow-pill text-[12.5px]">
            <span className="text-muted-foreground">From</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-7 w-[140px] border-0 bg-transparent shadow-none ring-0 px-1 text-[12.5px]"
            />
            <span className="text-muted-foreground">To</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-7 w-[140px] border-0 bg-transparent shadow-none ring-0 px-1 text-[12.5px]"
            />
          </div>

          <div className="ml-auto inline-flex items-center gap-0 p-1 rounded-full bg-white/55 dark:bg-white/[0.06] backdrop-blur-md ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-pill">
            <TabPill active={mode === 'flat'} onClick={() => setMode('flat')}>Flat</TabPill>
            <TabPill active={mode === 'per_employee'} onClick={() => setMode('per_employee')}>Per employee</TabPill>
          </div>
        </section>

        {/* Partial fan-out failure — some runs' payslips couldn't be fetched. */}
        {failedRuns > 0 && !loading && !error && (
          <p className="text-[12.5px] text-amber-700 dark:text-amber-400">
            {failedRuns} run{failedRuns === 1 ? "'s payslips" : "s' payslips"} could not be loaded — the totals
            and list below are incomplete. Reload to retry.
          </p>
        )}

        {/* Main list */}
        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {loading ? (
            <RowSkeleton />
          ) : error ? (
            <ErrorRow error={error} />
          ) : filtered.length === 0 ? (
            hasAnyFilter ? <EmptyFiltered /> : <EmptyNoData />
          ) : mode === 'flat' ? (
            <FlatList rows={filtered} currency={currency} employeeById={employeeById} />
          ) : (
            <PerEmployeeList rows={filtered} currency={currency} employeeById={employeeById} />
          )}
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────── lists ─────────────────────────── */

function FlatList({
  rows, currency, employeeById,
}: { rows: Row[]; currency: string; employeeById: Record<string, EmployeeWithUser> }) {
  // Sort: most recent period first, then employee name.
  const sorted = [...rows].sort((a, b) => {
    const pe = b.run.period_end.localeCompare(a.run.period_end);
    if (pe !== 0) return pe;
    const an = employeeById[a.employee_id]?.name ?? a.employee_id;
    const bn = employeeById[b.employee_id]?.name ?? b.employee_id;
    return an.localeCompare(bn);
  });

  return (
    <ul className="divide-y divide-border/40">
      {sorted.map((r, i) => {
        const emp = employeeById[r.employee_id];
        const name = emp?.name ?? r.employee_id.slice(0, 8);
        const { tone, label } = statusFor(r);
        return (
          <motion.li
            key={r.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.015, 0.18) }}
          >
            <Link
              to={`/dashboard/payroll/payslips/${r.id}`}
              className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
            >
              <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[14px] font-bold text-saffron-foreground shrink-0">
                {(name?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{name}</p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  {formatDate(r.run.period_start)} – {formatDate(r.run.period_end)}
                  {emp?.designation ? ` · ${emp.designation}` : ''}
                </p>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                <span className="text-[10.5px] text-muted-foreground tabular-nums">
                  Gross {formatMoney(r.gross_additions, currency)}
                </span>
                <span className="font-sans font-semibold text-[15px] tabular-nums tracking-[-0.01em] text-foreground">
                  {formatMoney(r.net_amount, currency)}
                </span>
              </div>
              <span className={statusPill(tone)}>
                <span className={statusDot(tone)} />
                {label}
              </span>
            </Link>
          </motion.li>
        );
      })}
    </ul>
  );
}

function PerEmployeeList({
  rows, currency, employeeById,
}: { rows: Row[]; currency: string; employeeById: Record<string, EmployeeWithUser> }) {
  // Group by employee_id, sorted by name; within group, most recent first.
  const groups: Record<string, Row[]> = {};
  for (const r of rows) (groups[r.employee_id] ||= []).push(r);
  const sortedIds = Object.keys(groups).sort((a, b) => {
    const an = employeeById[a]?.name ?? a;
    const bn = employeeById[b]?.name ?? b;
    return an.localeCompare(bn);
  });

  return (
    <ul className="divide-y divide-border/40">
      {sortedIds.map((empId) => {
        const list = [...groups[empId]].sort((a, b) => b.run.period_end.localeCompare(a.run.period_end));
        const emp = employeeById[empId];
        const name = emp?.name ?? empId.slice(0, 8);
        const totalNet = list.reduce((s, r) => s + Number(r.net_amount), 0);
        return (
          <li key={empId} className="px-5 sm:px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[14px] font-bold text-saffron-foreground shrink-0">
                {(name?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{name}</p>
                <p className="text-[12px] text-muted-foreground mt-0.5 truncate">
                  {emp?.email ?? '—'} · {list.length} payslip{list.length === 1 ? '' : 's'}
                </p>
              </div>
              <span className="font-sans font-semibold text-[15px] tabular-nums tracking-[-0.01em] text-foreground">
                {formatMoney(totalNet, currency)}
              </span>
            </div>
            <ul className="mt-3 ml-[60px] grid gap-1.5">
              {list.map((r) => {
                const { tone, label } = statusFor(r);
                return (
                  <li key={r.id}>
                    <Link
                      to={`/dashboard/payroll/payslips/${r.id}`}
                      className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-muted/40 transition-colors"
                    >
                      <span className="text-[12.5px] text-muted-foreground tabular-nums flex-1 min-w-0 truncate">
                        {formatDate(r.run.period_start)} – {formatDate(r.run.period_end)}
                      </span>
                      <span className="text-[12.5px] font-mono tabular-nums text-foreground/85">
                        {formatMoney(r.net_amount, currency)}
                      </span>
                      <span className={statusPill(tone)}>
                        <span className={statusDot(tone)} />
                        {label}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

/* ─────────────────────────── tiles + bits ─────────────────────────── */

function SumTile({
  label, value, caption, accent = false,
}: { label: string; value: React.ReactNode; caption: string; accent?: boolean }) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <p className={VISUAL.eyebrow}>{label}</p>
      <p className={[
        'font-sans font-semibold leading-none tracking-[-0.03em] tabular-nums mt-3',
        accent ? 'text-[clamp(1.6rem,2.6vw,2.4rem)] text-foreground' : 'text-[clamp(1.4rem,2.2vw,2rem)] text-foreground',
      ].join(' ')}>
        {value}
      </p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function TabPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative inline-flex items-center px-4 h-8 text-[12px] font-semibold transition-colors rounded-full',
        active ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function RowSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
          <div className="h-5 w-20 rounded-full bg-muted/60 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function ErrorRow({ error }: { error: string }) {
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <p className="text-[15px] font-semibold text-foreground">Couldn't load payslips</p>
      <p className="text-[13px] text-muted-foreground mt-1.5">{error}</p>
    </div>
  );
}

function EmptyNoData() {
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
        <Banknote className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No payslips yet</p>
      <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
        Payslips are created when a payroll run is computed. Cut a run to start.
      </p>
      <div className="mt-6">
        <Link to="/dashboard/payroll">
          <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New run
          </Button>
        </Link>
      </div>
    </div>
  );
}

function EmptyFiltered() {
  return (
    <div className="py-14 px-8 text-center">
      <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center">
        <Search className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[15px] font-semibold text-foreground">No payslips match those filters</p>
      <p className="text-[13px] text-muted-foreground mt-1.5">Try widening the date range or clearing employee + status.</p>
    </div>
  );
}

/* ─────────────────────────── status helpers ─────────────────────────── */

// Labels reflect what the data actually tracks: `delivered_at` records the
// payslip EMAIL, not a bank payment — so "Emailed" / "Not sent", never "Paid".
// (Filter keys keep their historical names; only the visible labels changed.)
function statusFor(r: Row): { tone: StatusTone; label: string; key: 'pending' | 'paid' | 'on_hold' | 'cancelled' | 'draft' } {
  if (r.run.status === 'cancelled') return { tone: 'muted', label: 'Cancelled', key: 'cancelled' };
  // A paid run means the money actually moved — that outranks the email state.
  if (r.run.status === 'paid') return { tone: 'paid', label: 'Paid', key: 'paid' };
  if (r.delivered_at) return { tone: 'success', label: 'Emailed', key: 'paid' };
  if (r.run.distributed_at) return { tone: 'warning', label: 'Not sent', key: 'on_hold' };
  if (r.run.status === 'finalized') return { tone: 'info', label: 'Pending', key: 'pending' };
  return { tone: 'warning', label: 'Draft', key: 'draft' };
}
